import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import {
  getNotifications,
  getUnreadCount,
  markAllRead as markAllReadApi,
  dismissNotification as dismissNotificationApi,
  clearAllNotifications as clearAllNotificationsApi,
  type Notification,
} from "@/lib/api/notifications";

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  markAllRead: () => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  clearAll: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useNotifications(userId: string | null): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useRef(true);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    try {
      const [notifs, count] = await Promise.all([
        getNotifications(20),
        getUnreadCount(),
      ]);
      if (isMounted.current) {
        setNotifications(notifs);
        setUnreadCount(count);
      }
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [userId]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Realtime subscription
  useEffect(() => {
    if (!userId) return;

    let isSubscribed = false;
    const channel = supabase
      .channel(`notifications-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
        },
        (payload) => {
          if (payload.new) {
            const newNotification = payload.new as Notification;
            // RLS already filters to this user's rows, but double-check
            if (newNotification.user_id !== userId) return;
            setNotifications((prev) => [newNotification, ...prev].slice(0, 20));
            setUnreadCount((prev) => prev + 1);
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "notifications",
        },
        (payload) => {
          if (payload.new) {
            const updated = payload.new as Notification;
            if (updated.user_id !== userId) return;
            setNotifications((prev) =>
              prev.map((n) => (n.id === updated.id ? updated : n))
            );
          }
        }
      )
      .subscribe((status, err) => {
        if (status === "SUBSCRIBED") {
          isSubscribed = true;
          console.log("[Notifications] Realtime subscribed");
        }
        if (err) {
          console.error("[Notifications] Realtime error", err);
        }
      });

    return () => {
      // Only remove channel if subscription completed successfully
      // Otherwise use unsubscribe to gracefully handle cleanup
      if (isSubscribed) {
        supabase.removeChannel(channel);
      } else {
        channel.unsubscribe().catch(() => {
          // Ignore errors - prevents "Object Not Found" Sentry errors
        });
      }
    };
  }, [userId]);

  // Cleanup ref
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const markAllRead = useCallback(async () => {
    if (unreadCount === 0) return;
    try {
      await markAllReadApi();
      setNotifications((prev) =>
        prev.map((n) => (n.read ? n : { ...n, read: true }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all read", err);
    }
  }, [unreadCount]);

  const dismiss = useCallback(async (id: string) => {
    try {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      await dismissNotificationApi(id);
    } catch (err) {
      console.error("Failed to dismiss notification", err);
      fetchNotifications(); // Re-fetch on error to restore state
    }
  }, [fetchNotifications]);

  const clearAll = useCallback(async () => {
    try {
      setNotifications([]);
      setUnreadCount(0);
      await clearAllNotificationsApi();
    } catch (err) {
      console.error("Failed to clear notifications", err);
      fetchNotifications();
    }
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAllRead,
    dismiss,
    clearAll,
    refresh: fetchNotifications,
  };
}
